import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get, find, cloneDeep } from 'lodash';
import CancellationPolicy from './cancellation-policy';
import {
  CancelSummaryCalculatorFnb,
  CancelSummaryCalculator,
  CancelContractSummaryCalculator,
  HostCancelSummaryCalculator,
  HostCancelContractSummaryCalculator,
  HostCancelSummaryCalculatorFnb
} from './summary-cancel-detail';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import { BookingSummary } from '../elements-summaries/booking-summary/BookingSummary';

class CancellationSummaryMain extends React.Component {
  constructor(props) {
    super(props);

    this.closeSummary = this.closeSummary.bind(this);
    this.cancelSummaryCalc = this.cancelSummaryCalc.bind(this);
    this.redirectToInbox = this.redirectToInbox.bind(this);
  }

  redirectToInbox() {
    const { booking } = this.props;
    const bookingId = get(booking, 'booking._id', '');

    this.context.router.push({ pathname: `/inbox/${bookingId}` });
  }

  closeSummary() {
    const { closeIOSModal, booking } = this.props;

    if (closeIOSModal) {
      return closeIOSModal();
    }

    const bookingId = get(booking, 'booking._id', '');
    this.context.router.push({ pathname: `/inbox/${bookingId}` });
  }

  cancelSummaryCalc(viewAs) {
    const { contracts, cancellationObj = {} } = this.props;
    const booking = get(this.props, 'booking.booking', {});
    const currencyType = get(booking, 'listing.prices[0].currency');
    const previousSpaceRental = find(cancellationObj.previous, {
      type: 'SPACE_RENTAL'
    });
    const previousBooking = get(previousSpaceRental, 'meta.booking', {});
    const paymentDetails = get(cancellationObj, 'payment_changes', {});

    const creditRefund = get(paymentDetails, 'credit_amount', 0);
    const cashRefund = get(paymentDetails, 'refund_amount', 0);
    const creditOnlyRefund = 0 < creditRefund && !cashRefund;

    if (previousBooking) {
      previousBooking.listing = booking.listing;
    }

    if (isEmpty(cancellationObj)) {
      return;
    }

    if ('host' === viewAs.toLowerCase()) {
      if ('FANDB' === booking.type || 'FANDB' === previousBooking.type) {
        return (
          <HostCancelSummaryCalculatorFnb
            bookingObj={cancellationObj.old_values || previousBooking}
            payoutBookingObj={isEmpty(contracts) ? cancellationObj : booking}
            currencyType={currencyType}
          />
        );
      }
      if (!isEmpty(contracts)) {
        return (
          <HostCancelContractSummaryCalculator
            contractObj={cancellationObj.previous}
            payoutBookingObj={cancellationObj.contracts}
            currencyType={currencyType}
          />
        );
      }
      return (
        <HostCancelSummaryCalculator
          bookingObj={cancellationObj.old_values}
          payoutBookingObj={cancellationObj}
          currencyType={currencyType}
        />
      );
    }

    if ('FANDB' === booking.type || 'FANDB' === previousBooking.type) {
      return (
        <CancelSummaryCalculatorFnb
          bookingObj={cancellationObj.old_values || previousBooking}
          refundBookingObj={cancellationObj}
          currencyType={currencyType}
        />
      );
    }
    if (!isEmpty(contracts)) {
      return (
        <CancelContractSummaryCalculator
          contractObj={cancellationObj.previous}
          refundContractObj={cancellationObj}
          currencyType={currencyType}
          paymentDetails={paymentDetails}
          creditRefundSelected={creditOnlyRefund}
        />
      );
    }
    return (
      <CancelSummaryCalculator
        bookingObj={cancellationObj.old_values}
        refundBookingObj={cancellationObj}
        currencyType={currencyType}
      />
    );
  }

  render() {
    const {
      profile,
      contracts,
      cancellationObj = {},
      displayError,
      intl
    } = this.props;
    const spaceRentalContract = find(contracts, { type: 'SPACE_RENTAL' }) || {};
    const cancelDetails =
      get(spaceRentalContract, 'meta.cancel_details', null) || cancellationObj;

    // flag for bookings cancelled for credit during covid recovery
    const isCovidCreditCancel =
      'credit' === get(cancelDetails, 'cancellation_policy.id', '');

    const creditRefundAmount = get(
      cancellationObj,
      'payment_changes.credit_amount',
      0
    );
    const cashRefundAmount = get(
      cancellationObj,
      'payment_changes.refund_amount',
      0
    );
    const creditOnlyRefund = 0 < creditRefundAmount && !cashRefundAmount;

    const booking = get(this.props, 'booking.booking', {});
    const isGuest = get(booking, 'guest.ssoid') === profile.SSOid;
    const viewAs = isGuest ? 'guest' : 'host';
    const isFnb = 'FANDB' === booking.type;
    const cancellationReason =
      booking.guest_reason || booking.host_reason || 'Unspecified';
    const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();

    const activityDetailBooking = cloneDeep(booking);
    if (!isEmpty(activityDetailBooking)) {
      activityDetailBooking.payment = {};
      activityDetailBooking.list_price = {};
      activityDetailBooking.payout = {};
      activityDetailBooking.listing.use_type = null;
      if (isGuest) {
        activityDetailBooking.view_as = 'GUEST';
        activityDetailBooking.guest = null;
      } else {
        activityDetailBooking.view_as = 'HOST';
        activityDetailBooking.host = null;
      }
    }

    const refundText = {
      '0%': this.props.intl.formatMessage(
        {
          id: 'inbox_id_cancellation_summary.cancellation_summary_main.refunded',
          defaultMessage:
            '{isFnb} are refunded if the booking has not taken place.'
        },
        {
          isFnb: isFnb
            ? this.props.intl.formatMessage({
                id: 'inbox_id_cancellation_summary.cancellation_summary_main.tax',
                defaultMessage: 'Tax and tip'
              })
            : this.props.intl.formatMessage({
                id: 'inbox_id_cancellation_summary.cancellation_summary_main.fees',
                defaultMessage: 'Cleaning fees'
              })
        }
      ),
      '50%': this.props.intl.formatMessage(
        {
          id: 'inbox_id_cancellation_summary.cancellation_summary_main.partially_refunded',
          defaultMessage:
            'Guest has been partially refunded. {taxFees} are refunded if the booking has not taken place.'
        },
        { taxFees: isFnb ? 'Tax and tip' : 'Cleaning fees' }
      ),
      '100%': this.props.intl.formatMessage({
        id: 'inbox_id_cancellation_summary.cancellation_summary_main.fully_refunded',
        defaultMessage: 'Guest has been fully refunded'
      })
    };

    return (
      <div className='page cancellation-summary-container'>
        <div className='container-fluid'>
          <div className='row' style={{ marginTop: 16 }}>
            <div
              className='col-xs-12 col-md-4 col-lg-offset-2 col-md-offset-1'
              style={{ paddingLeft: 15 }}
            >
              <h3>
                <FormattedMessage
                  id='inbox_id_cancellation_summary.cancellation_summary_main.cancellation_summary'
                  defaultMessage='Cancellation Summary'
                />
              </h3>
            </div>
          </div>
        </div>

        <div className='container-fluid'>
          <div className='row activity-detail-container'>
            <div className='col-xs-12 col-md-4 col-md-push-7 col-lg-3'>
              <BookingSummary
                booking={activityDetailBooking}
                profile={profile}
                intl={intl}
              />
              {isEmpty(cancellationObj) ||
              cancelDetails.is_old_policy_cancel ||
              isCovidCreditCancel ? null : (
                <CancellationPolicy
                  cancelUpdateObj={cancelDetails}
                  pageView='cancelSummary'
                  viewAs={viewAs}
                  isFnb={isFnb}
                  creditRefundSelected={creditOnlyRefund}
                />
              )}
            </div>

            <div className='col-xs-12 col-md-6 col-md-pull-3 col-lg-5 col-lg-pull-1'>
              <div className='cancellation-summary-details'>
                {/*show covid copy if covid credit cancel, otherwise show reason with guest credit copy if necessary*/}
                {isCovidCreditCancel ? (
                  <div>
                    {isGuest ? (
                      <p>
                        <FormattedMessage
                          id='inbox_id_cancellation_summary.cancellation_summary_main.credit_issued'
                          defaultMessage='Peerspace Credit has been issued for this cancellation.'
                        />
                        <a
                          href={`https://support.peerspace.com/hc/${zendeskLocale}/articles/360041426311-How-do-I-redeem-Peerspace-credit-`}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {' '}
                          <FormattedMessage
                            id='inbox_id_cancellation_summary.cancellation_summary_main.learn_more'
                            defaultMessage='Learn More'
                          />
                        </a>
                      </p>
                    ) : (
                      <p>
                        <FormattedMessage
                          id='inbox_id_cancellation_summary.cancellation_summary_main.learn_more_booking_refunded'
                          defaultMessage='This booking was refunded in full due to COVID-19. {link}'
                          values={{
                            link: (
                              <a
                                href={`https://support.peerspace.com/hc/${zendeskLocale}/articles/360040650612-Flexible-cancellations-in-response-to-COVID-19`}
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                <FormattedMessage
                                  id='inbox_id_cancellation_summary.cancellation_summary_main.learn_more_link'
                                  defaultMessage='Learn More'
                                />
                              </a>
                            )
                          }}
                        />
                      </p>
                    )}
                  </div>
                ) : (
                  <div>
                    {!!creditRefundAmount && isGuest && (
                      <div>
                        <p>
                          <FormattedMessage
                            id='inbox_id_cancellation_summary.cancellation_summary_main.credit_issued_link'
                            defaultMessage='Peerspace Credit has been issued for this cancellation. {link}'
                            values={{
                              link: (
                                <a
                                  href={`https://support.peerspace.com/hc/${zendeskLocale}/articles/360041426311-How-do-I-redeem-Peerspace-credit-`}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  <FormattedMessage
                                    id='inbox_id_cancellation_summary.cancellation_summary_main.learn_more_guest'
                                    defaultMessage='Learn More'
                                  />
                                </a>
                              )
                            }}
                          />
                        </p>
                        <hr />
                      </div>
                    )}
                    <h5 className='margin-lg-top margin-xl-bottom'>
                      <FormattedMessage
                        id='inbox_id_cancellation_summary.cancellation_summary_main.cancellation_reason'
                        defaultMessage='Cancellation reason'
                      />
                    </h5>
                    <div className='cancellation-reason'>
                      {cancellationReason}
                    </div>
                  </div>
                )}

                <hr />
                <h5 className='margin-lg-top margin-xl-bottom'>
                  <FormattedMessage
                    id='inbox_id_cancellation_summary.cancellation_summary_main.guest_summary'
                    defaultMessage='{guest} Summary'
                    values={{
                      guest: isGuest
                        ? this.props.intl.formatMessage({
                            id: 'inbox_id_cancellation_summary.cancellation_summary_main.refund',
                            defaultMessage: 'Refund'
                          })
                        : this.props.intl.formatMessage({
                            id: 'inbox_id_cancellation_summary.cancellation_summary_main.payout',
                            defaultMessage: 'Payout'
                          })
                    }}
                  />
                </h5>

                {this.cancelSummaryCalc(viewAs)}

                <p className='text-gray-dark margin-lg-top'>
                  {!isGuest && !isCovidCreditCancel
                    ? refundText[cancelDetails.refund_percentage]
                    : ''}
                </p>
                {!!displayError && (
                  <div className='help-block text-danger text-left'>
                    <FormattedMessage
                      id='inbox_id_cancellation_summary.cancellation_summary_main.something_wrong'
                      defaultMessage='Something went wrong, please try again.'
                    />
                  </div>
                )}
                <button
                  className='classic btn btn-default btn-extra-padding margin-xl-top'
                  style={{ maxWidth: 150 }}
                  onClick={this.closeSummary}
                >
                  <FormattedMessage
                    id='inbox_id_cancellation_summary.cancellation_summary_main.close'
                    defaultMessage='Close'
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

CancellationSummaryMain.propTypes = {
  accessToken: PropTypes.string,
  profile: PropTypes.string,
  closeIOSModal: PropTypes.func,
  booking: PropTypes.object,
  contracts: PropTypes.object,
  displayError: PropTypes.string,
  cancellationObj: PropTypes.object,
  intl: intlShape
};

CancellationSummaryMain.contextTypes = {
  router: PropTypes.object
};

exports.CancellationSummaryMain = injectIntl(CancellationSummaryMain);
